import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import EmptyData from "../../components/EmptyData";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

const REQUESTS_PAGE_LIMIT = 500;

const normalizeText = (value) => String(value || "").trim();
const normalizeState = (value) => normalizeText(value).toLowerCase();

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeTimestamp = (value) => {
  if (!value) {
    return Date.now();
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return parsed;
  }

  const dateValue = new Date(value).getTime();
  return Number.isFinite(dateValue) ? dateValue : Date.now();
};

const normalizeRequestRecord = (request) => {
  const requestId = String(request?.payment_key || request?.requestId || request?._id || request?.id || "");

  return {
    ...request,
    id: request?.id || request?._id || requestId,
    requestId,
    payment_key: request?.payment_key || requestId,
    token: toNumber(request?.token, 0),
    currentTimeStamp: normalizeTimestamp(
      request?.currentTimeStamp || request?.createdAt || request?.updatedAt,
    ),
    status: String(request?.status || "pending").toLowerCase(),
    user: request?.user || null,
  };
};

const formatTimestamp = (value) => {
  const raw = Number(value);

  if (!raw) {
    return "--";
  }

  const milliseconds = raw < 1e12 ? raw * 1000 : raw;
  return new Date(milliseconds).toLocaleString();
};

const getStatusMeta = (status) => {
  const normalized = String(status || "pending").toLowerCase();

  if (normalized === "approved") {
    return { label: "APPROVED", bg: "#EAF7EF", fg: "#16803C" };
  }

  if (normalized === "rejected") {
    return { label: "REJECTED", bg: "#FDECEC", fg: "#C62828" };
  }

  return { label: "PENDING", bg: "#FFF5E5", fg: "#A96C00" };
};

const getActionButtonStyles = (variant) => {
  const common = {
    minWidth: 112,
    px: 2,
    py: 0.95,
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "0.01em",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
    transition: "all 180ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 14px 28px rgba(15, 23, 42, 0.16)",
    },
  };

  if (variant === "approve") {
    return {
      ...common,
      color: "#fff",
      background: "linear-gradient(135deg, #27B36A 0%, #0F8B4C 100%)",
      "&:hover": {
        ...common["&:hover"],
        background: "linear-gradient(135deg, #2BCB78 0%, #0D7A43 100%)",
      },
    };
  }

  return {
    ...common,
    color: "#fff",
    background: "linear-gradient(135deg, #FF7A7A 0%, #D83A3A 100%)",
    "&:hover": {
      ...common["&:hover"],
      background: "linear-gradient(135deg, #FF8E8E 0%, #C92B2B 100%)",
    },
  };
};

const columns = [
  {
    Header: <span style={{ color: "blue" }}>Sr</span>,
    accessor: "sr",
    width: "5%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Request ID</span>,
    accessor: "requestId",
    width: "12%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Name</span>,
    accessor: "name",
    width: "16%",
    align: "left",
  },
  {
    Header: <span style={{ color: "blue" }}>Email</span>,
    accessor: "email",
    width: "16%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Phone</span>,
    accessor: "phone",
    width: "12%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Gender</span>,
    accessor: "gender",
    width: "8%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>State</span>,
    accessor: "state",
    width: "10%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Request Time</span>,
    accessor: "requestTime",
    width: "14%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Bank Name</span>,
    accessor: "bankName",
    width: "12%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Bank Account Number</span>,
    accessor: "bankAccountNumber",
    width: "15%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Amount in Tokens</span>,
    accessor: "amountTokens",
    width: "10%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>N Equivalent</span>,
    accessor: "nEquivalent",
    width: "10%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Remaining Tokens</span>,
    accessor: "remainingTokens",
    width: "10%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Status</span>,
    accessor: "status",
    width: "10%",
    align: "center",
  },
  {
    Header: <span style={{ color: "blue" }}>Action</span>,
    accessor: "action",
    width: "16%",
    align: "center",
  },
];

const PaymentHistory = () => {
  const getUserState = useSelector((state) => state?.data?.getUserState);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  const getData = useCallback(async () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

    if (!localStorage.getItem("user")) {
      navigate("/authentication/sign-in");
      return;
    }

    const isSuperAdmin = Boolean(storedUser?.superAdmin);
    const storedState = normalizeText(storedUser?.state);
    const selectedState = normalizeText(getUserState);
    const stateFilter = isSuperAdmin
      ? selectedState && selectedState !== "All State"
        ? selectedState
        : ""
      : storedState;

    setCheckSuperAdmin(isSuperAdmin);
    setAdminState(storedState);
    setLoading(true);

    try {
      const params = {
        page: 1,
        limit: REQUESTS_PAGE_LIMIT,
      };

      if (stateFilter) {
        params.state = stateFilter;
      }

      const response = await apiClient.get("/subscriptions/payment-requests", { params });
      const payload = response?.data ?? response;
      const requests = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      const normalizedRequests = requests.map(normalizeRequestRecord);

      setAllData(
        normalizedRequests.sort((a, b) => (b?.currentTimeStamp || 0) - (a?.currentTimeStamp || 0)),
      );
    } catch (error) {
      console.error("[PaymentHistory] failed to fetch payment history", error);
      toast.error(error?.message || "Failed to load payment history");
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }, [getUserState, navigate]);

  const handleApproveReject = useCallback(
    async (notification, action) => {
      const requestId = notification?.payment_key || notification?.requestId || notification?.id;

      if (!requestId) {
        toast.error("Missing payment request id");
        return;
      }

      try {
        await apiClient.patch(`/subscriptions/payment-request/${requestId}/status`, {
          status: action,
        });

        toast.success(`Payment request ${action} successfully.`);

        socketService.emit("sendcustompushnotification", {
          userId: notification?.userId,
          title: action === "approved" ? "Cashout Approved" : "Cashout Declined",
          message:
            action === "approved"
              ? "Your encash request has been approved. Please check it."
              : "Your encash request has been declined. Please contact support.",
          action: "cashout_status",
        });

        await getData();
      } catch (error) {
        toast.error(error?.message || "Something went wrong.");
      }
    },
    [getData],
  );

  useEffect(() => {
    const user = localStorage.getItem("user");

    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    void getData();

    const refreshFromSocket = () => {
      void getData();
    };

    void socketService.initializeSocket().catch(() => { });
    socketService.on("paymentrequest", refreshFromSocket);

    return () => {
      socketService.off("paymentrequest", refreshFromSocket);
    };
  }, [getData, navigate]);

  const filteredData = allData.filter((item) => {
    const itemState = normalizeState(item?.user?.state || item?.user?.userState || item?.state);
    const activeState = normalizeState(
      checkSuperAdmin
        ? normalizeText(getUserState) && normalizeText(getUserState) !== "All State"
          ? getUserState
          : ""
        : adminState,
    );
    const name = item?.user?.userName?.toLowerCase() || "";
    const email = item?.user?.userEmail?.toLowerCase() || "";
    const phone = item?.user?.userPhone?.toLowerCase() || "";
    const requestId = String(item?.requestId || item?.payment_key || "").toLowerCase();
    const status = String(item?.status || "pending").toLowerCase();
    const term = searchTerm.toLowerCase();

    return (
      (!activeState || itemState === activeState) &&
      (name.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        requestId.includes(term) ||
        status.includes(term))
    );
  });

  const displayStateLabel = checkSuperAdmin
    ? normalizeText(getUserState) && normalizeText(getUserState) !== "All State"
      ? normalizeText(getUserState).toUpperCase()
      : "ALL STATE"
    : normalizeText(adminState).toUpperCase();

  const tableRows = useMemo(
    () =>
      filteredData.map((notification, index) => {
        const statusMeta = getStatusMeta(notification?.status);

        return {
          sr: index + 1,
          requestId: notification?.requestId || "--",
          name: (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <Avatar
                src={notification?.user?.userImage}
                alt={notification?.user?.userName}
                sx={{ width: 34, height: 34, flexShrink: 0 }}
              />
              <MDTypography
                component="span"
                variant="body2"
                sx={{
                  minWidth: 0,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  fontWeight: 600,
                  color: "#0F172A",
                }}
              >
                {notification?.user?.userName || "--"}
              </MDTypography>
            </Box>
          ),
          email: notification?.user?.userEmail || "--",
          phone: notification?.user?.userPhone || "--",
          gender: notification?.user?.userGender || "--",
          state: notification?.user?.state || notification?.user?.userState || "--",
          requestTime: formatTimestamp(notification?.currentTimeStamp),
          bankName: notification?.user?.payment?.bankName || "--",
          bankAccountNumber: notification?.user?.payment?.accountNumber || "--",
          amountTokens: notification?.token ?? "--",
          nEquivalent:
            notification?.nEquivalent ??
            notification?.amountEquivalent ??
            notification?.token ??
            "--",
          remainingTokens: notification?.user?.token ?? "--",
          status: (
            <Chip
              size="small"
              label={statusMeta.label}
              sx={{
                minWidth: 98,
                borderRadius: 999,
                fontWeight: 700,
                bgcolor: statusMeta.bg,
                color: statusMeta.fg,
                "& .MuiChip-label": {
                  px: 1.25,
                },
              }}
            />
          ),
          action:
            notification?.status === "pending" || !notification?.status ? (
              <Stack direction="row" spacing={1.2} justifyContent="center" alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
                  sx={getActionButtonStyles("approve")}
                  onClick={() => handleApproveReject(notification, "approved")}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CancelOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={getActionButtonStyles("reject")}
                  onClick={() => handleApproveReject(notification, "rejected")}
                >
                  Reject
                </Button>
              </Stack>
            ) : (
              <MDTypography component="span" variant="body2">
                --
              </MDTypography>
            ),
        };
      }),
    [filteredData, handleApproveReject],
  );

  return (
    <DashboardLayout>
      <MDBox
        pt={7}
        pb={3}
        sx={{
          background:
            "radial-gradient(circle at top right, rgba(76, 129, 255, 0.08), transparent 32%), linear-gradient(180deg, rgba(255,255,255,0.45), rgba(255,255,255,0))",
        }}
      >
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={5} px={2}>
          <TextField
            label="Search by name, email, phone, request, or status"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 320 },
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: "14px",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
              },
            }}
          />
        </Box>

        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(15, 23, 42, 0.12)",
              }}
            >
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                sx={{
                  textAlign: "center",
                  borderRadius: "18px",
                  background: "linear-gradient(135deg, #4f8ef7 0%, #346de0 100%)",
                  boxShadow: "0 18px 36px rgba(52, 109, 224, 0.28)",
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              >
                <ToastContainer />
                <MDTypography
                  variant="h6"
                  color="white"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.1rem", md: "1.5rem" },
                    letterSpacing: "0.02em",
                  }}
                >
                  {displayStateLabel} PAYMENT HISTORY DASHBOARD
                </MDTypography>
                <MDTypography
                  variant="h2"
                  color="white"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    opacity: 0.95,
                  }}
                >
                  Admin payment request management
                </MDTypography>
              </MDBox>

              {loading ? (
                <Box
                  sx={{
                    minHeight: "40vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : filteredData.length > 0 ? (
                <MDBox sx={{ px: 2, pb: 2 }}>
                  <DataTable
                    table={{ columns, rows: tableRows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <EmptyData />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default PaymentHistory;
